/**
 * useEvidenceLineage — Citation_Traceability_Architecture CT2 (Stage 1).
 *
 * Unified read over the four lineage targets — SED variable, SED
 * section, report claim, checklist row — projecting whatever exists
 * on disk into the canonical `EvidenceLineage` shape. Read-only:
 * NEVER mutates source data.
 *
 * Why a pure resolver + thin React wrapper:
 *   - The resolver (`resolveEvidenceLineage`) is a pure function of its
 *     inputs. That makes it trivial to unit-test without React, and
 *     lets Stage-2 DOCX/PDF exporters reuse it server-side.
 *   - The hook (`useEvidenceLineage`) is the React-binding wrapper. It
 *     reads exclusively from the Zustand store, never from props that
 *     drift on every render — so consumers can call it inside render
 *     without re-running the projection on every keystroke.
 *
 * Empty-state contract:
 *   - The resolver NEVER throws. When nothing on disk pertains to a
 *     target, it returns `emptyLineage(target)` (empty `sources` array,
 *     confidence `0`).
 *   - When something pertains, the resolver returns the projection
 *     blended with the user-override on top (if any).
 *
 * Confidence rule (Stage 1 — placeholder):
 *   The blended confidence is `max(sources.map(sourceConfidence))`
 *   capped at 1. Stage-2 may swap in a fancier blend (e.g. weighted
 *   by source-kind, downgraded by anomaly flags). The field is on the
 *   type today so consumers don't have to special-case its absence.
 *
 * Foundation discipline:
 *   - No side effects, no Firestore, no fetch.
 *   - Every adapter call goes through `src/types/citation.ts`. This
 *     file does NOT inline projection logic.
 *   - The `EvidenceSection` access pattern below is the *only* place
 *     where the resolver crosses into the existing scattered shapes;
 *     the citation type stays UI-/storage-neutral.
 */

import { useMemo } from 'react';
import { currentDocumentFieldSource } from './sed/documentFieldView';
import { useStore } from '../store';
import { STABLE_EMPTY_ARRAY } from '../store/stableEmpty';
import {
  documentToSource,
  providerToSource,
  amendmentToSource,
  siteContextClaimToSource,
  regionalBaselineToSource,
  amendmentBackingDocumentSource,
  emptyLineage,
  clampConfidence,
  type EvidenceLineage,
  type EvidenceSource,
  type LineageTarget,
} from '../types/citation';
import type { SiteEvidenceDocument, SedSectionId, EvidenceSection } from '../types/siteEvidence';
import type { SEDAmendment } from '../types/sedAmendments';
import type { SiteContext } from '../types/siteContext';
import type { SiteDocument, SiteRagReport } from '../types/siteRag';
import {
  matchUploadedDocsToChecklist,
  type ChecklistMatchResult,
} from '../config/documentChecklists';
import type { ReportTypeId } from '../config/reportTypes';

/* ── Input bundle ────────────────────────────────────────────────────── */

/**
 * Everything the resolver needs to project a target. All fields are
 * optional so callers can pass partial bundles — missing data yields
 * empty lineage, not a crash.
 *
 *  - `sed`        the active Site Evidence Document.
 *  - `amendments` user amendments against `sed` (filtered to the
 *                  active SED by the caller).
 *  - `siteContext` user-declared site-context block (claims/conflicts).
 *  - `documents`  the site's uploaded documents.
 *  - `report`     the active report (for report-claim lineage).
 *  - `reportType` the active report type (for checklist lineage). When
 *                  omitted, checklist-row targets always return empty.
 *  - `tier3Lookup` optional fn returning `{ baselineKey, credibility }`
 *                  for a checklist row that is Tier-3 auto-satisfied.
 *                  Returning `null` means "not auto-satisfied".
 *                  Caller wires this to the substitute-registry; the
 *                  resolver stays free of that import to keep Stage 1
 *                  decoupled from EA5 runtime.
 */
export interface LineageInputs {
  sed?: SiteEvidenceDocument | null;
  amendments?: SEDAmendment[];
  siteContext?: SiteContext | null;
  documents?: SiteDocument[];
  report?: SiteRagReport | null;
  reportType?: ReportTypeId;
  tier3Lookup?: (rowId: string) => { baselineKey: string; credibility: number } | null;
}

/* ── Helpers ─────────────────────────────────────────────────────────── */

/**
 * Read a per-source confidence value 0..1. Mirrors the Stage-1
 * placeholder blend rule above — Stage-2 may evolve this.
 */
function sourceConfidence(s: EvidenceSource): number {
  switch (s.kind) {
    case 'document':           return clampConfidence(s.relevanceScore ?? 0.5);
    case 'provider':           return 0.85; // provider data carries no per-source score on the projection
    case 'user-amendment':     return 0.95; // user-asserted, treated near-canonical
    case 'site-context-claim': return 0.8;  // user-declared, but lower than amendments which are explicit
    case 'regional-baseline':  return s.credibility;
    case 'derived':            return 0.6;
  }
}

/**
 * Walk the SED data structure by dot-path. Returns the
 * `EvidenceSection<T>` that *owns* the variable. We descend until we
 * hit a top-level section key — every SED variable lives under exactly
 * one section, e.g. `'seismic.data.pga'` → the `seismic` section.
 *
 * Returns the section + the resolved `sedSectionId` for downstream
 * provider-id lookup. `null` when the path doesn't match any known
 * section (legacy / typo).
 */
function sectionForVariablePath(
  sed: SiteEvidenceDocument,
  variablePath: string,
): { sectionId: SedSectionId; section: EvidenceSection<unknown> } | null {
  const head = variablePath.split('.')[0] as keyof SiteEvidenceDocument;
  if (!head) return null;
  const candidate = sed[head] as unknown;
  if (
    candidate &&
    typeof candidate === 'object' &&
    'source' in (candidate as Record<string, unknown>) &&
    'retrievedAt' in (candidate as Record<string, unknown>)
  ) {
    return {
      sectionId: head as SedSectionId,
      section: candidate as EvidenceSection<unknown>,
    };
  }
  return null;
}

/**
 * Map an `EvidenceSection.source` string to a canonical provider key.
 * Today the SED stores a human-readable label (e.g. `'NREL NSRDB'`);
 * we slug it for the projection. Stage-2's section editor will likely
 * stamp the canonical id directly, at which point this collapses to
 * an identity read.
 */
function providerKeyForSection(section: EvidenceSection<unknown>): string {
  return (section.source || 'unknown')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/**
 * Project an EvidenceSection's verified-doc layer (when present) onto
 * `document` sources. Documents_On_Site Prompt 11 leaves the doc-ids
 * on the section via `supportingDocIds`; we surface each one. Quotes
 * are NOT inlined at Stage 1 (the renderer fetches them on demand);
 * the relevance score, when present, is the section's
 * `docContributionScore` — that's the per-section relevance, not the
 * doc's per-report-type relevance, but it's the closest signal we
 * have for "how much this doc influenced THIS field".
 */
function sectionDocSources(section: EvidenceSection<unknown>): EvidenceSource[] {
  const ids = section.supportingDocIds ?? [];
  if (ids.length === 0) return [];
  const score = section.docContributionScore;
  return ids.map(id => documentToSource({ id }, undefined, score));
}

/**
 * Project an EvidenceSection's `linkedDocumentIds` onto `document`
 * sources. These are user-linked docs that strengthen the section.
 * Distinct from `supportingDocIds` (which carry an automatic
 * contribution score from the doc-aware confidence layer).
 */
function sectionLinkedDocSources(section: EvidenceSection<unknown>): EvidenceSource[] {
  const ids = section.linkedDocumentIds ?? [];
  if (ids.length === 0) return [];
  return ids.map(id => documentToSource({ id }));
}

/* ── Resolver: SED variable ──────────────────────────────────────────── */

function resolveSedVariable(
  variablePath: string,
  { sed, amendments, siteContext }: LineageInputs,
): EvidenceLineage {
  const target: LineageTarget = { kind: 'sed-variable', id: variablePath };
  if (!sed) return emptyLineage(target);

  const sources: EvidenceSource[] = [];
  let userOverride: EvidenceLineage['userOverride'];

  // 1) Provider data — the section that owns this variable.
  const sectionRef = sectionForVariablePath(sed, variablePath);
  if (sectionRef && sectionRef.section.status !== 'unavailable') {
    const { section } = sectionRef;
    sources.push(
      providerToSource(providerKeyForSection(section), section.data, section.retrievedAt),
    );
    // Doc-aware confidence layer (Documents_On_Site Prompt 11).
    const fieldSource = currentDocumentFieldSource(section, variablePath.split('.').slice(2).join('.'));
    if (fieldSource) sources.push(documentToSource({ id: fieldSource.docId }, fieldSource.excerptPreview, fieldSource.confidence));
    else if (variablePath.split('.').slice(2).length === 1) {
      // Existing flat rows retain legacy section lineage. Newly exposed nested
      // leaves require their own source, rather than borrowing another field's doc.
      sources.push(...sectionDocSources(section));
      sources.push(...sectionLinkedDocSources(section));
    }
  }

  // 2) Site-context claims that point at this exact field.
  if (siteContext) {
    for (const claim of siteContext.claims ?? []) {
      if (claim.sedField === variablePath) {
        sources.push(
          siteContextClaimToSource(claim, claim.sedField, siteContext.updatedAt),
        );
      }
    }
  }

  // 3) User amendments. The most-recent amendment becomes the
  //    `userOverride`; older amendments stay in `sources` for history.
  const matching = (amendments ?? [])
    .filter(a => a.variablePath === variablePath)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  for (const amd of matching) {
    sources.push(amendmentToSource(amd));
    // If the amendment cites a document, surface it as a sibling
    // document source so the renderer can hyperlink to it.
    const backing = amendmentBackingDocumentSource(amd.source);
    if (backing) sources.push(backing);
  }
  const latest = matching[matching.length - 1];
  if (latest && latest.status !== 'rejected') {
    userOverride = {
      newValue: latest.newValue,
      reason: latest.reason,
      overriddenAt: latest.createdAt,
      overriddenBy: latest.createdBy,
    };
  }

  const confidence = sources.length === 0
    ? 0
    : clampConfidence(Math.max(...sources.map(sourceConfidence)));
  return userOverride
    ? { target, sources, confidence, userOverride }
    : { target, sources, confidence };
}

/* ── Resolver: SED section ───────────────────────────────────────────── */

function resolveSedSection(
  sectionId: string,
  { sed, siteContext }: LineageInputs,
): EvidenceLineage {
  const target: LineageTarget = { kind: 'sed-section', id: sectionId };
  if (!sed) return emptyLineage(target);

  const sed_ = sed as unknown as Record<string, unknown>;
  const candidate = sed_[sectionId];
  if (
    !candidate ||
    typeof candidate !== 'object' ||
    !('source' in (candidate as Record<string, unknown>))
  ) {
    return emptyLineage(target);
  }
  const section = candidate as EvidenceSection<unknown>;

  const sources: EvidenceSource[] = [];
  if (section.status !== 'unavailable') {
    sources.push(
      providerToSource(providerKeyForSection(section), section.data, section.retrievedAt),
    );
  }
  sources.push(...sectionDocSources(section));
  sources.push(...sectionLinkedDocSources(section));

  // Site-context claims that target any variable under this section.
  if (siteContext) {
    for (const claim of siteContext.claims ?? []) {
      if (claim.sedField && claim.sedField.startsWith(`${sectionId}.`)) {
        sources.push(
          siteContextClaimToSource(claim, claim.sedField, siteContext.updatedAt),
        );
      }
    }
  }

  const confidence = sources.length === 0
    ? 0
    : clampConfidence(Math.max(...sources.map(sourceConfidence)));
  return { target, sources, confidence };
}

/* ── Resolver: report claim ──────────────────────────────────────────── */

/**
 * `report.references` carries `DataSource[]` — each entry binds to a
 * `field` string. The "claim id" today is the index-as-string of the
 * inline `[Ref N]` token (1-based, matching the report's existing
 * citation convention). Stage-3 widens this when real claim ids land.
 */
function resolveReportClaim(
  claimId: string,
  { report }: LineageInputs,
): EvidenceLineage {
  const target: LineageTarget = { kind: 'report-claim', id: claimId };
  if (!report) return emptyLineage(target);

  // `references` is the canonical claim->source map on the report.
  // We accept either a numeric token ('1', '2', …) which maps to
  // `references[idx - 1]`, or a free-form claim id matched against
  // `DataSource.field`.
  const refs = (report as unknown as { references?: { field: string; source: string; url?: string; retrievedAt: string }[] }).references ?? [];

  const idxMatch = /^\d+$/.test(claimId) ? refs[Number(claimId) - 1] : undefined;
  const fieldMatches = refs.filter(r => r.field === claimId);
  const hits = idxMatch ? [idxMatch] : fieldMatches;
  if (hits.length === 0) return emptyLineage(target);

  const sources: EvidenceSource[] = hits.map(r =>
    providerToSource(
      r.source.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
      { field: r.field, url: r.url },
      r.retrievedAt,
    ),
  );
  return {
    target,
    sources,
    confidence: clampConfidence(Math.max(...sources.map(sourceConfidence))),
  };
}

/* ── Resolver: checklist row ─────────────────────────────────────────── */

function resolveChecklistRow(
  rowId: string,
  { documents, reportType, tier3Lookup }: LineageInputs,
): EvidenceLineage {
  const target: LineageTarget = { kind: 'checklist-row', id: rowId };
  if (!reportType) return emptyLineage(target);

  const sources: EvidenceSource[] = [];

  // 1) Uploaded docs matched to this row (and to any row that
  //    `replaceableBy` lists this one through).
  const match: ChecklistMatchResult = matchUploadedDocsToChecklist(documents ?? [], reportType);
  const direct = match.matched.get(rowId) ?? [];
  for (const doc of direct) {
    const rel = doc.relevance?.[reportType];
    sources.push(documentToSource(doc, undefined, rel));
  }

  // 2) Tier-3 baseline auto-satisfaction (EA5). Pure: the caller
  //    supplies the lookup; the resolver doesn't import the registry.
  if (sources.length === 0 && tier3Lookup) {
    const tier3 = tier3Lookup(rowId);
    if (tier3) {
      sources.push(regionalBaselineToSource(tier3.baselineKey, tier3.credibility));
    }
  }

  if (sources.length === 0) return emptyLineage(target);
  return {
    target,
    sources,
    confidence: clampConfidence(Math.max(...sources.map(sourceConfidence))),
  };
}

/* ── Public resolver entry point ─────────────────────────────────────── */

/**
 * Pure projection. Stage-2 surfaces and Stage-3 exporters import this
 * directly so they don't have to mount the React store.
 *
 * Never throws. Every code path either returns a populated
 * `EvidenceLineage` or `emptyLineage(target)`.
 */
export function resolveEvidenceLineage(
  target: LineageTarget,
  inputs: LineageInputs,
): EvidenceLineage {
  switch (target.kind) {
    case 'sed-variable':   return resolveSedVariable(target.id, inputs);
    case 'sed-section':    return resolveSedSection(target.id, inputs);
    case 'report-claim':   return resolveReportClaim(target.id, inputs);
    case 'checklist-row':  return resolveChecklistRow(target.id, inputs);
  }
}

/* ── React hook binding ──────────────────────────────────────────────── */

/**
 * Hook variant — same projection, but reads inputs from the Zustand
 * store keyed by `siteId` + `reportId` (when relevant).
 *
 * Selector discipline (per the worktree's Zustand stability doctrine):
 * each `useStore` call subscribes to ONE root slice. Derivation runs
 * inside a `useMemo` keyed on the slice identities; nothing inside the
 * selector calls `.find` / `.filter` so shallow-equality holds.
 *
 * Returns `EvidenceLineage` synchronously — never null, never throws.
 *
 * @param target   What to project.
 * @param siteId   The active site id. Pass `null` to short-circuit to
 *                 empty.
 * @param reportId Optional active report id. Required for
 *                 `report-claim` and `checklist-row` targets when the
 *                 caller needs the report-type binding.
 * @param tier3Lookup Optional tier-3 substitute lookup. The hook
 *                  doesn't import the registry; pass the lookup if
 *                  you want EA5 auto-satisfaction in the projection.
 */
export function useEvidenceLineage(
  target: LineageTarget,
  siteId: string | null | undefined,
  reportId?: string | null,
  tier3Lookup?: LineageInputs['tier3Lookup'],
): EvidenceLineage {
  // Subscribe to roots only; derive inside useMemo.
  const sites = useStore(s => s.siteRag?.sites ?? STABLE_EMPTY_ARRAY);
  const reports = useStore(s => s.siteRag?.reports ?? STABLE_EMPTY_ARRAY);
  const amendments = useStore(s => s.amendmentState?.amendments ?? STABLE_EMPTY_ARRAY);

  return useMemo(() => {
    if (!siteId) return emptyLineage(target);
    const site = sites.find(s => s.id === siteId);
    if (!site) return emptyLineage(target);

    const report = reportId ? reports.find(r => r.id === reportId) ?? null : null;

    const inputs: LineageInputs = {
      sed: (site as { sed?: SiteEvidenceDocument | null }).sed ?? null,
      amendments,
      siteContext: (site as { siteContext?: SiteContext | null }).siteContext ?? null,
      documents: (site as { documents?: SiteDocument[] }).documents ?? [],
      report,
      reportType: report?.reportType,
      tier3Lookup,
    };
    return resolveEvidenceLineage(target, inputs);
    // `target.id` + `target.kind` are primitives — listed individually so
    // memo doesn't bust on every render when a caller inlines the object.
  }, [target.kind, target.id, sites, reports, amendments, siteId, reportId, tier3Lookup]);
}
